import { useEffect, useMemo, useState } from 'react';
import { WandSparkles } from 'lucide-react';
import type { Node } from '@/stores/workflowStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SmartBlockInputDialogProps {
  node: Node | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SmartBlockInputDialog({ node, open, onOpenChange }: SmartBlockInputDialogProps) {
  const updateNode = useWorkflowStore((s) => s.updateNode);
  const autoConfigureNode = useWorkflowStore((s) => s.autoConfigureNode);
  const requirements = useMemo(() => (node ? getSmartBlockInputRequirements(node) : []), [node]);
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!node) {
      setDraft({});
      return;
    }
    const next: Record<string, string> = {};
    requirements.forEach((req) => {
      next[req.key] = String(node.config[req.key] ?? '');
    });
    setDraft(next);
  }, [node, requirements]);
  useEffect(() => {
    if (!node && open) onOpenChange(false);
  }, [node, onOpenChange, open]);

  if (!node || !open) return null;

  const apply = () => {
    const payload: Record<string, string> = {};
    requirements.forEach((req) => {
      const value = (draft[req.key] ?? '').trim();
      if (value) payload[req.key] = value;
    });
    if (Object.keys(payload).length === 0) {
      toast.message('Add at least one value to continue');
      return;
    }
    updateNode(node.id, payload);
    const changes = autoConfigureNode(node.id);
    if (changes.length > 0) {
      toast.success('Smart Blocks completed this block');
    } else {
      toast.success('Block updated');
    }
    onOpenChange(false);
  };

  return (
    <aside
      className={cn(
        'fixed right-4 top-[calc(var(--app-top-offset,3.5rem)+1rem)] z-[70] w-[min(92vw,38rem)]',
        'rounded-lg border bg-background p-4 shadow-lg'
      )}
      role="dialog"
      aria-modal="false"
      aria-label="Smart Blocks needs human input"
    >
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-base font-semibold leading-none tracking-tight">
          <WandSparkles className="h-4 w-4 text-orange-500" />
          Smart Blocks needs human input
        </p>
        <p className="text-sm text-muted-foreground">
          This block is auto-configured but still needs the required values below.
        </p>
      </div>

      <div className="mt-3 space-y-3 max-h-[52vh] overflow-auto pr-1">
        {requirements.map((req) => (
          <div key={req.key} className="rounded-md border border-orange-400/50 bg-orange-500/5 p-3 space-y-2">
            <div>
              <Label className="text-sm">{req.label}</Label>
              <p className="text-[11px] text-muted-foreground">{req.reason}</p>
            </div>
            {req.inputKind === 'textarea' ? (
              <textarea
                value={draft[req.key] ?? ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, [req.key]: e.target.value }))}
                placeholder={req.placeholder}
                className="w-full rounded border border-input bg-background p-2 text-xs min-h-[84px]"
                rows={4}
              />
            ) : (
              <Input
                value={draft[req.key] ?? ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, [req.key]: e.target.value }))}
                placeholder={req.placeholder}
                className="text-xs"
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Later
        </Button>
        <Button onClick={apply}>Apply inline fixes</Button>
      </div>
    </aside>
  );
}
